import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ApprovalList, ApprovalView } from '@agentic-os/contracts';
import { StructuredValue } from '@/components/assessed-view';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Button,
  ErrorNote,
  Field,
  Mono,
  PageHeader,
  Table,
  Td,
  Th,
} from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';

type Filter = 'pending' | 'approved' | 'rejected' | '';

/** Human decisions that executions are waiting for (ADR-031). */
export function ApprovalsPage() {
  const [filter, setFilter] = useState<Filter>('pending');
  const [selected, setSelected] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['approvals', filter],
    queryFn: () =>
      api<ApprovalList>(`/api/v1/approvals?limit=100${filter ? `&status=${filter}` : ''}`),
    refetchInterval: 15_000,
  });
  const items = q.data?.items ?? [];
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Approvals"
        description="Executions paused until a person decides. Approving resumes the execution; rejecting stops it (or lets it continue on its rejection path)."
      />
      <div className="flex gap-2">
        {(['pending', 'approved', 'rejected', ''] as const).map((f) => (
          <Button
            key={f || 'all'}
            variant={filter === f ? 'primary' : 'default'}
            onClick={() => {
              setFilter(f);
              setSelected(null);
            }}
          >
            {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
          </Button>
        ))}
      </div>
      <Card>
        <CardBody className="p-0">
          {q.isError ? (
            <div className="p-4">
              <ErrorNote error={q.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Requested</Th>
                  <Th>Decision to take</Th>
                  <Th>Workflow</Th>
                  <Th>Requested by</Th>
                  <Th>Classification</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    className="cursor-pointer hover:bg-surface-muted"
                    onClick={() => setSelected(a.id)}
                  >
                    <Td>{formatTime(a.createdAt)}</Td>
                    <Td>{a.title}</Td>
                    <Td>
                      <Mono>
                        {a.workflow.key} v{a.workflow.version}
                      </Mono>
                    </Td>
                    <Td>{a.requestedBy.email ?? '—'}</Td>
                    <Td>{a.classification}</Td>
                    <Td>
                      <DomainStatus value={a.status} />
                    </Td>
                  </tr>
                ))}
                {!q.isPending && items.length === 0 && (
                  <tr>
                    <Td colSpan={6} className="text-center text-muted">
                      Nothing here.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
      {selected && <ApprovalDetail id={selected} />}
    </div>
  );
}

function ApprovalDetail({ id }: { id: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['approval', id],
    queryFn: () => api<ApprovalView>(`/api/v1/approvals/${id}`),
  });
  const [note, setNote] = useState('');
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      api<ApprovalView>(`/api/v1/approvals/${id}/decision`, {
        method: 'POST',
        body: { decision, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: () => {
      setNote('');
      void qc.invalidateQueries({ queryKey: ['approval', id] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
  const a = q.data;
  if (q.isError) return <ErrorNote error={q.error} />;
  if (!a) return null;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{a.title}</CardTitle>
          <DomainStatus value={a.status} />
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <Field
            label="Execution"
            value={
              <Link
                to="/executions/$executionId"
                params={{ executionId: a.executionId }}
                className="text-accent underline"
              >
                <Mono>{a.executionId}</Mono>
              </Link>
            }
          />
          <Field label="Step" value={<Mono>{a.stepKey}</Mono>} />
          <Field label="Requested by" value={a.requestedBy.email ?? '—'} />
          {a.decidedAt && (
            <>
              <Field label="Decided by" value={a.decidedBy?.email ?? '—'} />
              <Field label="Decided" value={formatTime(a.decidedAt)} />
              <Field label="Note" value={a.note ?? '—'} />
            </>
          )}
        </div>
        {a.instructions && <p className="text-sm">{a.instructions}</p>}
        <div>
          <p className="text-xs font-medium text-muted">What you are deciding on</p>
          <div className="mt-1 max-h-[32rem] overflow-auto rounded-md border border-border p-3">
            <StructuredValue value={a.subject} />
          </div>
        </div>
        {a.status === 'pending' && can('approvals.decide') && (
          <div className="space-y-2">
            <label className="block text-xs text-muted">
              Note (optional, kept with the decision)
              <textarea
                className="mt-1 w-full rounded-md border border-border bg-surface p-2 text-sm"
                rows={3}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <Button
                variant="primary"
                disabled={decide.isPending}
                onClick={() => decide.mutate('approve')}
              >
                Approve and resume
              </Button>
              <Button
                variant="danger"
                disabled={decide.isPending}
                onClick={() => decide.mutate('reject')}
              >
                Reject
              </Button>
            </div>
            {decide.isError && <ErrorNote error={decide.error} />}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
